import { store, observable, action, computed } from '~/common/mobx.decorator'
import { authStore, routerStore } from '~/stores'
import { MAESTRO_VERSION } from '../recruiter-v2.constants'
import { classicEquivalent, v2Equivalent } from './maestro-version.helper'

const STORAGE_PREFIX = 'maestro_version:'

/*
 * The choice belongs to the user, not the browser, so it is keyed by user id and
 * survives signing out and back in.
 *
 * ⚠️ BE contract (request B7): this is a localStorage stand-in for a per-user
 * preference on the API, which is what makes the choice follow the recruiter to
 * another device. Only `readAll` and `write` know where it lives — swapping them
 * for the API read/write is the whole migration.
 */
const readAll = () => {
	const preferences = {}
	try {
		for (let index = 0; index < window.localStorage.length; index += 1) {
			const key = window.localStorage.key(index)
			if (key && key.startsWith(STORAGE_PREFIX)) {
				preferences[key.slice(STORAGE_PREFIX.length)] = window.localStorage.getItem(key)
			}
		}
	} catch (error) {
		// Storage can be blocked (private windows, site data off). The switch still
		// works for the session; it just is not remembered.
	}
	return preferences
}

const write = (userId, version) => {
	try {
		window.localStorage.setItem(`${STORAGE_PREFIX}${userId}`, version)
	} catch (error) {
		// see readAll
	}
}

@store()
class MaestroVersionStore {
	@observable preferences = readAll()

	@computed
	get prefersV2() {
		const userId = authStore.id
		return Boolean(userId) && this.preferences[userId] === MAESTRO_VERSION.v2
	}

	/**
	 * Only an explicit "Classic" counts. On a tenant with V2 released, V2 is the
	 * default: someone who never touched the switch lands in V2 too.
	 */
	@computed
	get prefersClassic() {
		const userId = authStore.id
		return Boolean(userId) && this.preferences[userId] === MAESTRO_VERSION.classic
	}

	@action
	setVersion = (version) => {
		const userId = authStore.id
		if (!userId) return
		this.preferences = { ...this.preferences, [userId]: version }
		write(userId, version)
	}

	/**
	 * Save the choice and move to the same screen in the other version, so
	 * switching never costs a recruiter their place.
	 */
	@action
	switchTo = (version, pathname) => {
		this.setVersion(version)
		return routerStore.goPage(version === MAESTRO_VERSION.v2 ? v2Equivalent(pathname) : classicEquivalent(pathname))
	}
}

export const maestroVersionStore = new MaestroVersionStore()
